import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";

const EMPTY = {
  name: "", advertiser: "", image_url: "", target_url: "", html_code: "",
  position: "header", outlet_ids: [], starts_at: "", ends_at: "", weight: 1, active: true, notes: "",
};

const POSITIONS = ["header", "sidebar", "in-article", "footer", "popup", "sticky-mobile"];

export default function Banners() {
  const { data, mutate } = useSWR("/banners", fetcher);
  const { data: outlets } = useSWR("/outlets", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, weight: Number(form.weight) || 1 };
      if (form.id) await api.put(`/banners/${form.id}`, payload);
      else await api.post("/banners", payload);
      toast.success("Anuncio guardado");
      setOpen(false);
      setForm(EMPTY);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/banners/${id}`).catch((e) => toast.error(errMsg(e)));
    mutate();
  };

  return (
    <div>
      <PageHeader
        kicker="Monetización"
        title="Gestor de anuncios y banners"
        description="Campañas publicitarias por medio, posición y vigencia, con métricas de impresiones y clics."
      >
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-banner-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo anuncio
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="banners-grid">
        {(data || []).map((b) => (
          <div key={b.id} className="border border-border bg-card overflow-hidden" data-testid={`banner-${b.id}`}>
            {b.image_url ? (
              <img src={b.image_url} alt={b.name} className="h-32 w-full object-cover" />
            ) : (
              <div className="h-32 grid place-items-center bg-muted text-muted-foreground">
                <Megaphone className="h-6 w-6" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={b.active ? "default" : "secondary"}>{b.active ? "Activo" : "Pausado"}</Badge>
                <span className="kicker">{b.position}</span>
              </div>
              <h3 className="text-lg font-semibold">{b.name}</h3>
              <p className="text-sm text-muted-foreground">{b.advertiser}</p>
              <div className="text-xs mono text-muted-foreground mt-2">
                {b.impressions || 0} impresiones · {b.clicks || 0} clics ·
                CTR {b.impressions ? ((b.clicks / b.impressions) * 100).toFixed(2) : 0}%
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => { setForm({ ...EMPTY, ...b }); setOpen(true); }} data-testid={`edit-banner-${b.id}`}>
                  Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(b.id)} data-testid={`delete-banner-${b.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {data?.length === 0 && (
          <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center md:col-span-2 xl:col-span-3">
            No hay anuncios registrados.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="banner-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Anuncio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="banner-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Anunciante</Label>
              <Input value={form.advertiser} onChange={(e) => set("advertiser", e.target.value)} className="mt-1.5" data-testid="banner-advertiser-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Imagen (URL)</Label>
              <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="mt-1.5" data-testid="banner-image-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Enlace destino</Label>
              <Input value={form.target_url} onChange={(e) => set("target_url", e.target.value)} className="mt-1.5" data-testid="banner-target-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Posición</Label>
              <select className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                value={form.position} onChange={(e) => set("position", e.target.value)} data-testid="banner-position-select">
                {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Inicio</Label>
                <Input type="date" value={(form.starts_at || "").slice(0, 10)} onChange={(e) => set("starts_at", e.target.value)} className="mt-1.5" data-testid="banner-start-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Fin</Label>
                <Input type="date" value={(form.ends_at || "").slice(0, 10)} onChange={(e) => set("ends_at", e.target.value)} className="mt-1.5" data-testid="banner-end-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Medios</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(outlets || []).map((o) => {
                  const on = (form.outlet_ids || []).includes(o.id);
                  return (
                    <button key={o.id} type="button"
                      onClick={() => set("outlet_ids", on ? form.outlet_ids.filter((x) => x !== o.id) : [...(form.outlet_ids || []), o.id])}
                      className={`text-xs px-3 py-1.5 border transition-colors ${on ? "bg-accent text-accent-foreground border-accent" : "border-border"}`}
                      data-testid={`banner-outlet-${o.id}`}>
                      {o.name}
                    </button>
                  );
                })}
              </div></div>
            <div><Label className="text-xs uppercase tracking-wider">Código HTML / AdSense (opcional)</Label>
              <Textarea rows={3} value={form.html_code} onChange={(e) => set("html_code", e.target.value)} className="mt-1.5 mono text-xs" data-testid="banner-html-input" /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="banner-active-switch" />
              <span className="text-sm">Anuncio activo</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.name} data-testid="save-banner-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
